import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config";

const SuperAdminDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenValue, setTokenValue] = useState("");
  const [tokenError, setTokenError] = useState("");

  // Obtener clientes desde la API
  const getClients = async () => {
    try {
      setLoading(true);
      console.log("Solicitando clientes desde el backend...");
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log("Clientes recibidos:", data);
      setClients(data);
    } catch (error) {
      console.error("Error al obtener los clientes:", error);
      setClients([]);
      setMessage("Error al obtener los clientes.");
    } finally {
      setLoading(false);
    }
  };

  const openTokenModal = (client) => {
    setSelectedClient(client);
    setConfirmPassword("");
    setTokenValue("");
    setTokenError("");
    setTokenModalVisible(true);
  };

  const fetchToken = async () => {
    if (!confirmPassword || !selectedClient?.id) {
      setTokenError("Password y cliente son obligatorios.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/client-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cliente_id: selectedClient.id,
          password: confirmPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setTokenValue(result.auth_token || "No disponible");
        setTokenError("");
      } else {
        setTokenValue("");
        setTokenError(result.error || "Error al obtener token.");
      }
    } catch (error) {
      setTokenValue("");
      setTokenError("Error al conectar con el servidor.");
    }
  };

  // Enviar correo de activación al cliente
  const sendEmail = async () => {
    if (!email || !selectedClient?.merchant_id) {
      setMessage("Merchant ID y email son obligatorios.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          merchant_id: selectedClient.merchant_id,
          email,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        console.log("Correo enviado exitosamente.");
        setMessage("Correo enviado exitosamente.");
      } else {
        console.error("Error al enviar el correo:", result.error);
        setMessage(`Error al enviar el correo: ${result.error}`);
      }
    } catch (error) {
      console.error("Error al conectar con el servidor:", error);
      setMessage("Error al conectar con el servidor.");
    }
  };

  // Mostrar modal para enviar el enlace
  const showModal = (client) => {
    console.log("Cliente seleccionado para envío:", client);

    if (!client?.merchant_id) {
      setMessage("El cliente no tiene un Merchant ID válido.");
      return;
    }

    setSelectedClient(client);
    setEmail(client.email || ""); // Prellenar el email si está disponible
    setModalVisible(true);
    setMessage("");
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    getClients();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
        Dashboard de Super Admin
      </h2>

      {message && (
        <p className="text-center text-red-500 font-medium mb-4">{message}</p>
      )}

      {loading ? (
        <p className="text-center text-gray-600">Cargando...</p>
      ) : (
        <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left">ID</th>
              <th className="py-3 px-6 text-left">Nombre</th>
              <th className="py-3 px-6 text-left">Merchant ID</th>
              <th className="py-3 px-6 text-left">Token</th>
              <th className="py-3 px-6 text-left">Estado</th>
              <th className="py-3 px-6 text-left">Próximo Pago</th>
              <th className="py-3 px-6 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm font-light">
            {clients.map((client) => (
              <tr
                key={client.id}
                className="border-b border-gray-200 hover:bg-gray-100"
              >
                <td className="py-3 px-6 text-left">{client.id}</td>
                <td className="py-3 px-6 text-left">
                  {client.nombre || client.merchant_id || "No disponible"}
                </td>
                <td className="py-3 px-6 text-left">
                  {client.merchant_id || "No disponible"}
                </td>
                <td className="py-3 px-6 text-left">
                  <button
                    onClick={() => openTokenModal(client)}
                    className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                  >
                    Ver Token
                  </button>
                </td>
                <td className="py-3 px-6 text-left">{client.estado}</td>
                <td className="py-3 px-6 text-left">
                  {client.fecha_proximo_pago || "No disponible"}
                </td>
                <td className="py-3 px-6 text-center">
                  <button
                    onClick={() => showModal(client)}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Enviar Enlace
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal para enviar el enlace */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-11/12 sm:w-96 p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Enviar Enlace a Merchant ID: {selectedClient?.merchant_id}
            </h3>
            <label className="block mb-4">
              <span className="text-gray-700">Email del Cliente:</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-blue-200"
                placeholder="Ingresa el email del cliente"
                required
              />
            </label>
            <div className="flex justify-end gap-4">
              <button
                onClick={sendEmail}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Enviar
              </button>
              <button
                onClick={() => setModalVisible(false)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para confirmar y ver token */}
      {tokenModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-11/12 sm:w-96 p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Confirmar Password de Super Admin
            </h3>
            <label className="block mb-4">
              <span className="text-gray-700">Password:</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring focus:ring-blue-200"
                placeholder="Ingresa tu password"
                required
              />
            </label>
            {tokenError && (
              <p className="text-red-500 text-sm mb-3">{tokenError}</p>
            )}
            {tokenValue && (
              <p className="text-gray-800 text-sm mb-3 break-all">
                Token: {tokenValue}
              </p>
            )}
            <div className="flex justify-end gap-4">
              <button
                onClick={fetchToken}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Confirmar
              </button>
              <button
                onClick={() => setTokenModalVisible(false)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
